import React from 'react';
import { Card, Spinner } from 'react-bootstrap';
import { CMWorkflowStatus } from '../../../../models/CatalogManagement';

interface SKUsByType {
  simple: number;
  variable: number;
  variation: number;
}

interface Statistics {
  totalByStatus: Record<string, number>;
  todayWPUploaded: number;
  todayCMRequested: number;
  todayPublished: number;
  thisWeekPublished: number;
  thisMonthPublished: number;
  totalPublishedSKUs: number;
  publishedSKUsByType: SKUsByType;
  totalDraftSKUs: number;
  draftSKUsByType: SKUsByType;
  waveCount: Record<string, number>;
}

interface StatisticsCardProps {
  statistics: Statistics;
  loading: boolean;
}

const StatCard: React.FC<{
  title: string;
  value: number;
  details?: React.ReactNode;
}> = ({ title, value, details }) => (
  <div
    style={{
      flex: 1,
      padding: '24px',
      background: '#ffffff',
      borderRadius: '6px',
      border: '1px solid #e5e7eb',
    }}
  >
    <div
      style={{
        fontSize: '0.75rem',
        color: '#6b7280',
        marginBottom: '12px',
        fontWeight: 600,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </div>
    <div style={{ fontSize: '2.25rem', fontWeight: 700, color: '#374151', lineHeight: 1 }}>{value}</div>
    {details && (
      <div style={{ fontSize: '0.875rem', color: '#4b5563', marginTop: '12px', fontWeight: 500 }}>{details}</div>
    )}
  </div>
);

export const StatisticsCard: React.FC<StatisticsCardProps> = ({ statistics, loading }) => {
  return (
    <Card className="mb-3" style={{ border: '1px solid #e5e7eb' }}>
      <Card.Body style={{ padding: '24px' }}>
        {loading ? (
          <div className="text-center py-2">
            <Spinner animation="border" size="sm" style={{ color: '#6b7280' }} />
            <span className="ms-2" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Loading statistics...
            </span>
          </div>
        ) : (
          <div className="d-flex gap-3 align-items-stretch">
            <StatCard
              title="Total WP Uploaded"
              value={statistics.totalByStatus[CMWorkflowStatus.WP_UPLOADED] || 0}
              details={<>Today: {statistics.todayWPUploaded}</>}
            />

            <StatCard
              title="Total CM Requested"
              value={statistics.totalByStatus[CMWorkflowStatus.CM_REQUESTED] || 0}
              details={<>Today: {statistics.todayCMRequested}</>}
            />

            <StatCard
              title="Total CM Published"
              value={statistics.totalByStatus[CMWorkflowStatus.CM_PUBLISHED] || 0}
              details={
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>Today: {statistics.todayPublished}</span>
                  <span>•</span>
                  <span>This Week: {statistics.thisWeekPublished}</span>
                  <span>•</span>
                  <span>This Month: {statistics.thisMonthPublished}</span>
                </div>
              }
            />

            <StatCard
              title="Draft SKUs"
              value={statistics.totalDraftSKUs}
              details={
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>Simple: {statistics.draftSKUsByType.simple}</span>
                  <span>•</span>
                  <span>Variable: {statistics.draftSKUsByType.variable}</span>
                  <span>•</span>
                  <span>Variation: {statistics.draftSKUsByType.variation}</span>
                </div>
              }
            />

            <StatCard
              title="Published SKUs"
              value={statistics.totalPublishedSKUs}
              details={
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span>Simple: {statistics.publishedSKUsByType.simple}</span>
                  <span>•</span>
                  <span>Variable: {statistics.publishedSKUsByType.variable}</span>
                  <span>•</span>
                  <span>Variation: {statistics.publishedSKUsByType.variation}</span>
                </div>
              }
            />
          </div>
        )}
      </Card.Body>
    </Card>
  );
};
